import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  CATEGORY_LABELS,
  detectOpenTag,
  filterTags,
  type EmotionTag,
} from "../lib/emotionTags";

interface RichScriptProps {
  text: string;
  onChange: (next: string) => void;
  placeholder?: string;
}

export interface RichScriptHandle {
  /** Focus the editor and place the caret at the end of its content. */
  focusEnd: () => void;
}

const TAG_RE = /\[([a-z][a-z\s-]{0,20})\]/gi;
const MAX_SUGGESTIONS = 8;

// Build DOM nodes from a plain-text string. Bracketed [tags] become
// non-editable chips; anything else is a text span.
function buildNodes(str: string): Node[] {
  const nodes: Node[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TAG_RE.lastIndex = 0;
  while ((m = TAG_RE.exec(str)) !== null) {
    if (m.index > last) {
      const span = document.createElement("span");
      span.textContent = str.slice(last, m.index);
      nodes.push(span);
    }
    const chip = document.createElement("span");
    chip.className = "tag-chip";
    chip.contentEditable = "false";
    chip.dataset.tag = m[0]; // includes brackets
    const name = document.createElement("span");
    name.className = "tag-chip-name";
    name.textContent = m[1];
    const close = document.createElement("button");
    close.type = "button";
    close.className = "tag-chip-x";
    close.setAttribute("aria-label", "Remove tag");
    close.textContent = "×";
    chip.appendChild(name);
    chip.appendChild(close);
    nodes.push(chip);
    last = m.index + m[0].length;
  }
  if (last < str.length) {
    const span = document.createElement("span");
    span.textContent = str.slice(last);
    nodes.push(span);
  }
  return nodes;
}

// Walk the editor and reconstruct the plain-text string, treating each
// chip as its bracketed `data-tag` value.
function readPlainText(root: HTMLElement): string {
  let out = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent ?? "";
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.classList?.contains("tag-chip")) {
        out += el.dataset.tag ?? "";
        return;
      }
      for (const child of Array.from(el.childNodes)) walk(child);
    }
  };
  walk(root);
  return out;
}

// Save caret as character offset in plain text equivalent.
function getCaretOffset(root: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.endContainer)) return null;

  let count = 0;
  let stopped = false;
  const walk = (node: Node): boolean => {
    if (stopped) return true;
    if (node === range.endContainer) {
      if (node.nodeType === Node.TEXT_NODE) count += range.endOffset;
      stopped = true;
      return true;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      count += node.textContent?.length ?? 0;
      return false;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.classList?.contains("tag-chip")) {
        count += el.dataset.tag?.length ?? 0;
        return false;
      }
      for (const child of Array.from(el.childNodes)) {
        if (walk(child)) return true;
      }
    }
    return false;
  };
  walk(root);
  return count;
}

function setCaretAtOffset(root: HTMLElement, offset: number) {
  let remaining = offset;
  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent?.length ?? 0;
      if (remaining <= len) {
        const sel = window.getSelection();
        if (!sel) return true;
        const range = document.createRange();
        range.setStart(node, remaining);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        return true;
      }
      remaining -= len;
      return false;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.classList?.contains("tag-chip")) {
        const len = el.dataset.tag?.length ?? 0;
        if (remaining <= len) {
          const sel = window.getSelection();
          if (!sel) return true;
          const range = document.createRange();
          range.setStartAfter(el);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          return true;
        }
        remaining -= len;
        return false;
      }
      for (const child of Array.from(el.childNodes)) {
        if (walk(child)) return true;
      }
    }
    return false;
  };
  walk(root);
}

export const RichScript = forwardRef<RichScriptHandle, RichScriptProps>(function RichScript(
  { text, onChange, placeholder = "Type the words you want spoken…" },
  externalRef,
) {
  const ref = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    externalRef,
    () => ({
      focusEnd: () => {
        const root = ref.current;
        if (!root) return;
        root.focus();
        // Place caret at the very end of the plain text (handles trailing
        // chips and trailing whitespace correctly).
        const plain = readPlainText(root);
        setCaretAtOffset(root, plain.length);
      },
    }),
    [],
  );
  const lastText = useRef<string>("");
  const pendingCaret = useRef<number | null>(null);

  const [open, setOpen] = useState(false);
  const [partial, setPartial] = useState("");
  const [openPos, setOpenPos] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [suggestions, setSuggestions] = useState<EmotionTag[]>([]);
  const suggestionsRef = useRef<EmotionTag[]>([]);

  // Re-render the editor when text prop changes from outside.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (text === lastText.current) return;
    const isFocused = document.activeElement === root;
    const offset =
      pendingCaret.current != null
        ? pendingCaret.current
        : isFocused
          ? getCaretOffset(root)
          : null;
    const nodes = buildNodes(text);
    root.replaceChildren(...nodes);
    lastText.current = text;
    if (offset != null) {
      if (pendingCaret.current != null && !isFocused) root.focus();
      setCaretAtOffset(root, Math.min(offset, text.length));
    }
    pendingCaret.current = null;
  }, [text]);

  const recompute = useCallback(() => {
    const root = ref.current;
    if (!root) {
      setOpen(false);
      return;
    }
    if (document.activeElement !== root) {
      setOpen(false);
      return;
    }
    const caret = getCaretOffset(root);
    if (caret == null) {
      setOpen(false);
      return;
    }
    const detected = detectOpenTag(text, caret);
    if (!detected) {
      setOpen(false);
      return;
    }
    const matches = filterTags(detected.partial).slice(0, MAX_SUGGESTIONS);
    if (matches.length === 0) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setPartial(detected.partial);
    setOpenPos(detected.openPos);
    const prev = suggestionsRef.current;
    const sameList =
      prev.length === matches.length &&
      prev.every((t, i) => t.tag === matches[i].tag);
    if (!sameList) {
      suggestionsRef.current = matches;
      setSuggestions(matches);
      // List changed → reset highlight to top.
      setHighlight(0);
    }
  }, [text]);

  // After text prop changes, recompute popover state.
  useEffect(() => {
    recompute();
  }, [recompute]);

  const insertTag = useCallback(
    (tag: EmotionTag) => {
      const root = ref.current;
      if (!root) return;
      const caret = getCaretOffset(root) ?? text.length;
      const before = text.slice(0, openPos);
      const after = text.slice(caret);
      // No literal trailing space — the chip's CSS margin handles visual
      // separation, and a literal space here was reading as a double-gap.
      const inserted = `[${tag.tag}]`;
      const next = before + inserted + after;
      pendingCaret.current = openPos + inserted.length;
      setOpen(false);
      lastText.current = "__force__";
      onChange(next);
    },
    [onChange, openPos, text],
  );

  const handleInput = () => {
    const root = ref.current;
    if (!root) return;
    const plain = readPlainText(root);
    const offset = getCaretOffset(root);
    const nodes = buildNodes(plain);
    root.replaceChildren(...nodes);
    if (offset != null) setCaretAtOffset(root, Math.min(offset, plain.length));
    lastText.current = plain;
    onChange(plain);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (open) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (suggestions[highlight]) insertTag(suggestions[highlight]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
    }

    // Forward-delete (Delete key) doesn't remove contenteditable=false chips
    // reliably across browsers — handle it ourselves when the caret sits
    // immediately before a tag.
    if (e.key === "Delete") {
      const root = ref.current;
      if (!root) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return;
      if (!root.contains(range.startContainer)) return;
      const caret = getCaretOffset(root);
      if (caret == null) return;
      const plain = readPlainText(root);
      const tagMatch = /^\[[a-z][a-z\s-]{0,20}\]/i.exec(plain.slice(caret));
      if (tagMatch) {
        e.preventDefault();
        let removeLen = tagMatch[0].length;
        if (plain[caret + removeLen] === " ") removeLen += 1;
        const next = plain.slice(0, caret) + plain.slice(caret + removeLen);
        pendingCaret.current = caret;
        lastText.current = "__force__";
        onChange(next);
      }
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target?.classList?.contains("tag-chip-x")) {
      e.preventDefault();
      const chip = target.closest(".tag-chip") as HTMLElement | null;
      if (!chip) return;
      const tag = chip.dataset.tag ?? "";
      const root = ref.current;
      if (!root) return;
      const plain = readPlainText(root);
      const next = plain.replace(tag + " ", "").replace(tag, "");
      lastText.current = "__force__"; // ensure effect re-renders
      onChange(next);
      return;
    }
    // Caret may have moved — re-evaluate the popover.
    requestAnimationFrame(recompute);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // While the popover is open, ↑/↓ are popover navigation, not caret moves.
    if (open && (e.key === "ArrowUp" || e.key === "ArrowDown")) return;
    // Caret-only keys (arrows, home, end) don't fire onInput, so re-check here.
    if (
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Home" ||
      e.key === "End"
    ) {
      recompute();
    }
  };

  const handleBlur = () => {
    // Defer so a mousedown on a suggestion can still fire insertTag.
    setTimeout(() => {
      if (document.activeElement !== ref.current) setOpen(false);
    }, 120);
  };

  return (
    <div className="rich-ta-wrap">
      <div
        ref={ref}
        className="ta rich-ta"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        role="textbox"
        aria-multiline="true"
      />
      {open && suggestions.length > 0 && (
        <div
          className="tag-suggest"
          // Don't steal focus from the editor on mousedown.
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="tag-suggest-head">
            Emotion tag
            {partial && <span className="tag-suggest-q"> · "{partial}"</span>}
          </div>
          {suggestions.map((tag, i) => (
            <button
              key={tag.tag}
              type="button"
              className={`tag-suggest-item${i === highlight ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                insertTag(tag);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span className="tag-suggest-name">[{tag.tag}]</span>
              <span className="tag-suggest-cat">
                {CATEGORY_LABELS[tag.category]}
              </span>
            </button>
          ))}
          <div className="tag-suggest-foot">
            ↑↓ navigate · Enter/Tab insert · Esc dismiss
          </div>
        </div>
      )}
    </div>
  );
});
